import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { on } from "@ember/modifier";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { concat, fn, array } from "@ember/helper";
import eq from "truth-helpers/helpers/eq";
import or from "truth-helpers/helpers/or";
import gt from "truth-helpers/helpers/gt";
import not from "truth-helpers/helpers/not";
import { debounce } from "@ember/runloop";
import { fetchProviders } from "../routes/proxyviews-directory";

const TYPES = ["residential", "datacenter", "mobile", "isp", "rotating"];
const SORTS = [
  { id: "rating", label: "Highest Rated" },
  { id: "newest", label: "Newest" },
  { id: "price_low", label: "Price: Low to High" },
  { id: "price_high", label: "Price: High to Low" },
  { id: "uptime", label: "Best Uptime" },
  { id: "reviews", label: "Most Reviewed" },
  { id: "pool_size", label: "Largest Pool" },
];

const isCompared = (list, item) => list && list.includes(item);

const formatNumber = (num) => {
  if (num === null || num === undefined || num === "") {
    return "—";
  }
  const n = Number(num);
  if (isNaN(n)) {
    return num;
  }
  return n.toLocaleString();
};

export default class ProxyviewsDirectoryBrowser extends Component {
  @tracked query = "";
  @tracked selectedTypes = [];
  @tracked sort = "rating";
  @tracked verifiedOnly = false;
  @tracked maxPrice = 30;
  @tracked compareList = [];
  @tracked page = 1;
  @tracked loading = false;
  @tracked errored = false;

  // Mobile filter sidebar: collapsed by default so provider results
  // appear first on small screens. Toggled by the "Filters" button.
  @tracked filtersOpen = false;

  @action
  toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
  }

  // Server-fetched results for the current filter/sort/page combination.
  // Starts out as whatever the route already loaded (page 1, default
  // filters), so there's no redundant fetch on first render.
  @tracked serverProviders = this.args.providers || [];
  @tracked meta = this.args.meta || {};

  types = TYPES;
  sorts = SORTS;

  // Cost slider is now a backend filter (sent via fetchProviders), so no
  // client-side filtering is needed here. Featured providers are pinned
  // to the front of whatever page/sort the server returned.
  get filtered() {
    return this.serverProviders.slice().sort((a, b) => {
      if (!!a.featured !== !!b.featured) {
        return a.featured ? -1 : 1;
      }
      return 0;
    });
  }

  get totalPages() {
    return this.meta.total_pages || 1;
  }

  get totalCount() {
    return this.meta.total_count ?? this.serverProviders.length;
  }

  get hasPrevPage() {
    return this.page > 1;
  }

  get hasNextPage() {
    return this.page < this.totalPages;
  }

  async runFetch(overrides = {}) {
    this.loading = true;
    this.errored = false;
    // Clear stale results so the skeleton doesn't render alongside the
    // previous page's cards during filtering/pagination.
    this.serverProviders = [];
    try {
      const { providers, meta } = await fetchProviders({
        q: this.query,
        types: this.selectedTypes,
        verified: this.verifiedOnly,
        sort: this.sort,
        page: this.page,
        maxPrice: this.maxPrice,
        ...overrides,
      });
      this.serverProviders = providers;
      this.meta = meta;
    } catch (e) {
      this.errored = true;
    } finally {
      this.loading = false;
    }
  }

  // Any change to search/type/verified/sort resets pagination back to page 1
  // and re-queries the backend — this is what actually searches the full
  // directory instead of just whatever page happened to be loaded already.
  resetAndFetch() {
    this.page = 1;
    this.runFetch();
  }

  @action updateQuery(e) {
    this.query = e.target.value;
    debounce(this, this.resetAndFetch, 250);
  }

  @action clearSearch() {
    this.query = "";
    this.resetAndFetch();
  }

  @action handleLogoError(e) {
    e.target.style.display = "none";
    const fallback = e.target.parentElement.querySelector('[class*="logo-placeholder"]');
    if (fallback) {
      fallback.style.display = "flex";
    }
  }

  @action checkLogoLoad(element) {
    if (element.complete && element.naturalWidth === 0) {
      this.handleLogoError({ target: element });
    }
  }

  @action toggleType(t) {
    if (this.selectedTypes.includes(t)) {
      this.selectedTypes = this.selectedTypes.filter((x) => x !== t);
    } else {
      this.selectedTypes = [...this.selectedTypes, t];
    }
    this.resetAndFetch();
  }

  @action setSort(e) {
    this.sort = e.target.value;
    this.resetAndFetch();
  }

  @action toggleVerified() {
    this.verifiedOnly = !this.verifiedOnly;
    this.resetAndFetch();
  }

  @action updatePrice(e) {
    this.maxPrice = parseInt(e.target.value, 10);
    // Now triggers a backend fetch (debounced) instead of client-side only.
    debounce(this, this.resetAndFetch, 350);
  }

  @action nextPage() {
    if (!this.hasNextPage || this.loading) {
      return;
    }
    this.page += 1;
    this.runFetch();
    this.scrollToResults();
  }

  @action prevPage() {
    if (!this.hasPrevPage || this.loading) {
      return;
    }
    this.page -= 1;
    this.runFetch();
    this.scrollToResults();
  }

  scrollToResults() {
    const grid = document.querySelector('.pd-grid');
    if (grid) {
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  @action toggleCompare(p) {
    if (this.compareList.includes(p)) {
      this.compareList = this.compareList.filter((x) => x !== p);
    } else {
      if (this.compareList.length >= 3) {
        alert("You can compare up to 3 providers at a time.");
        return;
      }
      this.compareList = [...this.compareList, p];
    }
  }

  @action clearCompare() {
    this.compareList = [];
  }

  get compareIds() {
    return this.compareList.map((p) => p.slug || p.id).join(",");
  }

  <template>
    <div class="pd-directory-container">
      <button type="button" class="pd-mobile-filters-toggle" {{on "click" this.toggleFilters}}>
        {{if this.filtersOpen "▼ Hide Filters" "▶ Filters"}}
      </button>
      <aside class="pd-directory-sidebar {{if this.filtersOpen "pd-directory-sidebar--open"}}" aria-label="Filters">
        <div class="pd-sidebar-section">
          <h4 class="pd-sidebar-title">Search</h4>
          <div class="pd-sidebar-search">
            <input
              type="text"
              placeholder="Search providers…"
              aria-label="Search providers"
              value={{this.query}}
              {{on "input" this.updateQuery}}
            />
            {{#if this.query}}
              <button type="button" class="pd-sidebar-search-clear" aria-label="Clear search" {{on "click" this.clearSearch}}>×</button>
            {{/if}}
          </div>
        </div>

        <div class="pd-sidebar-section">
          <h4 class="pd-sidebar-title">Proxy Type</h4>
          <div class="pd-sidebar-options">
            {{#each this.types as |t|}}
              <label class="pd-sidebar-label">
                <input
                  type="checkbox"
                  checked={{isCompared this.selectedTypes t}}
                  {{on "change" (fn this.toggleType t)}}
                />
                <span class="pd-sidebar-radio-custom"></span>
                <span class="pd-sidebar-option-name">{{t}}</span>
              </label>
            {{/each}}
          </div>
        </div>

        <div class="pd-sidebar-section">
          <h4 class="pd-sidebar-title">Max Cost / GB</h4>
          <div class="pd-sidebar-slider-container">
            <input
              type="range"
              min="1"
              max="30"
              step="1"
              value={{this.maxPrice}}
              class="pd-sidebar-slider"
              aria-label="Maximum cost per gigabyte"
              {{on "input" this.updatePrice}}
            />
            <div class="pd-sidebar-slider-values">
              <span>$1</span>
              <strong>{{if (eq this.maxPrice 30) "Any" (concat "$" this.maxPrice)}}</strong>
              <span>$30</span>
            </div>
          </div>
        </div>

        <div class="pd-sidebar-section">
          <h4 class="pd-sidebar-title">Verification</h4>
          <label class="pd-sidebar-label">
            <input
              type="checkbox"
              checked={{this.verifiedOnly}}
              {{on "change" this.toggleVerified}}
            />
            <span class="pd-sidebar-checkbox-custom"></span>
            <span class="pd-sidebar-option-name">✓ Verified Only</span>
          </label>
        </div>

        <div class="pd-sidebar-section">
          <h4 class="pd-sidebar-title">Sort By</h4>
          <select class="pd-sidebar-select" aria-label="Sort providers by" {{on "change" this.setSort}}>
            {{#each this.sorts as |s|}}
              <option value={{s.id}} selected={{eq this.sort s.id}}>{{s.label}}</option>
            {{/each}}
          </select>
        </div>
      </aside>

      <main class="pd-directory-main">
        {{#if this.loading}}
          <div class="pd-grid pd-grid--animated" aria-live="polite">
            <div class="pd-card-skeleton"><div class="skeleton-logo"></div><div class="skeleton-title"></div><div class="skeleton-line"></div><div class="skeleton-line short"></div></div>
            <div class="pd-card-skeleton"><div class="skeleton-logo"></div><div class="skeleton-title"></div><div class="skeleton-line"></div><div class="skeleton-line short"></div></div>
            <div class="pd-card-skeleton"><div class="skeleton-logo"></div><div class="skeleton-title"></div><div class="skeleton-line"></div><div class="skeleton-line short"></div></div>
          </div>
        {{/if}}

        {{#if this.errored}}
          <div class="pd-error" role="alert">
            Couldn't load providers. Try again in a moment.
          </div>
        {{/if}}

        <div class="pd-grid pd-grid--animated">
          {{#each this.filtered as |p index|}}
            <article class="pd-card pd-fade-up {{p.cardClass}} {{if p.verified 'pd-card--verified'}}">
              {{#if p.featured}}<span class="pd-featured-ribbon">Featured</span>{{/if}}
              <div class="pd-card__header">
                <div class="pd-card__logo">
                  {{#if p.logo}}
                    <img src={{p.logo}} alt={{p.name}} class="pd-card__logo-img" loading={{if (gt index 1) "lazy" "eager"}} fetchpriority={{if (eq index 0) "high" "auto"}} {{on "error" this.handleLogoError}} {{didInsert this.checkLogoLoad}} />
                    <span class="pd-card__logo-placeholder" style="display: none;">
                      {{p.logoInitial}}
                    </span>
                  {{else}}
                    <span class="pd-card__logo-placeholder">{{p.logoInitial}}</span>
                  {{/if}}
                </div>
                <div class="pd-card__title-block">
                  <h3 class="pd-card__name">
                    {{p.name}}
                    {{#if p.verified}}
                      <span class="pd-badge pd-badge--verified" title="Verified Provider">✓ Verified</span>
                    {{/if}}
                  </h3>
                  <div class="pd-card__types">
                    {{#each (or p.proxy_types (array p.proxy_type)) as |t|}}
                      <span class="pd-type-pill pd-type-{{t}}">{{t}}</span>
                    {{/each}}
                  </div>
                </div>
              </div>

              <div class="pd-card__rating-row">
                <span class="pd-card__rating-stars">★</span>
                <span class="pd-card__rating-value">{{p.ratingDisplay}}</span>
                <span class="pd-card__rating-count">({{p.review_count}} reviews)</span>
              </div>

              {{#if p.metadata.deal}}
                <div class="pd-card__promo-tag">
                  <span class="pd-card__promo-badge">
                    <span class="icon">🎁</span>
                    <span class="text" title={{p.metadata.deal}}>
                      {{#if p.metadata.coupon_code}}
                        <strong>{{p.metadata.coupon_code}}</strong>:
                      {{/if}}
                      {{p.metadata.deal}}
                    </span>
                  </span>
                </div>
              {{/if}}

              <div class="pd-card__badges-row">
                {{#if p.metadata.free_trial}}
                  <span class="pd-card__badge pd-card__badge--trial" title="Free Trial Available">
                    ⏳ {{p.metadata.free_trial}}
                  </span>
                {{/if}}
                {{#if p.metadata.speed}}
                  <span class="pd-card__badge pd-card__badge--speed" title="Tested Speed">
                    {{p.metadata.speed}}
                  </span>
                {{/if}}
                {{#if p.metadata.locations}}
                  <span class="pd-card__badge pd-card__badge--locations" title="Coverage">
                    🌎 {{p.metadata.locations}}
                  </span>
                {{/if}}
              </div>

              <div class="pd-card__metrics">
                <div class="pd-card__metric">
                  <span class="pd-card__metric-label">💵 Cost / GB</span>
                  <span class="pd-card__metric-value">{{p.costDisplay}}</span>
                </div>
                <div class="pd-card__metric">
                  <span class="pd-card__metric-label">⏱ Uptime</span>
                  <span class="pd-card__metric-value">{{p.uptime_percentage}}%</span>
                </div>
                <div class="pd-card__metric">
                  <span class="pd-card__metric-label">💧 IP Pool</span>
                  <span class="pd-card__metric-value">{{formatNumber p.ip_pool_size}}</span>
                </div>
                <div class="pd-card__metric">
                  <span class="pd-card__metric-label">⚡ Benchmark</span>
                  <span class="pd-card__metric-value">{{p.benchmarkDisplay}}</span>
                </div>
              </div>

              {{#if p.use_cases.length}}
                <div class="pd-card__use-cases">
                  {{#each p.use_cases as |uc|}}<span class="pd-chip">{{uc}}</span>{{/each}}
                </div>
              {{/if}}

              <div class="pd-card__footer">
                <a class="pd-card__cta" href={{p.providerUrl}}>View Profile →</a>
                {{#if p.affiliateUrl}}
                  <a class="pd-card__visit" href={{p.affiliateUrl}} rel="sponsored nofollow" target="_blank">Visit ↗</a>
                {{/if}}
                <label class="pd-card__compare-checkbox">
                  <input
                    type="checkbox"
                    checked={{isCompared this.compareList p}}
                    {{on "change" (fn this.toggleCompare p)}}
                  />
                  <span class="pd-card__compare-custom"></span>
                  Compare
                </label>
              </div>
            </article>
          {{else}}
            {{#unless this.loading}}
              <div class="pd-empty">
                <div class="pd-empty__icon">🔍</div>
                <div class="pd-empty__title">No providers match your filters</div>
                <div class="pd-empty__body">Try clearing the search or selecting "all" types.</div>
              </div>
            {{/unless}}
          {{/each}}
        </div>

        {{#unless (eq this.totalPages 1)}}
          <nav class="proxyviews-directory__pagination pd-pagination" aria-label="Provider results pages">
            <button
              type="button"
              aria-label="Previous page"
              disabled={{not this.hasPrevPage}}
              {{on "click" this.prevPage}}
            >
              ← Previous
            </button>
            <span class="pd-pagination__status">
              Page {{this.page}} of {{this.totalPages}} ({{this.totalCount}} providers)
            </span>
            <button
              type="button"
              aria-label="Next page"
              disabled={{not this.hasNextPage}}
              {{on "click" this.nextPage}}
            >
              Next →
            </button>
          </nav>
        {{/unless}}
      </main>
    </div>

    {{#if this.compareList.length}}
      <div class="pd-compare-toolbar">
        <div class="pd-compare-toolbar__inner">
          <span class="pd-compare-toolbar__text">
            Comparing <strong>{{this.compareList.length}}</strong> of 3 providers
          </span>
          <div class="pd-compare-toolbar__items">
            {{#each this.compareList as |cp|}}
              <span class="pd-compare-toolbar__chip">
                {{cp.name}}
                <button type="button" class="close" aria-label="Remove {{cp.name}} from comparison" {{on "click" (fn this.toggleCompare cp)}}>&times;</button>
              </span>
            {{/each}}
          </div>
          <div class="pd-compare-toolbar__actions">
            <button type="button" class="pd-btn pd-btn--text" {{on "click" this.clearCompare}}>Clear</button>
            <a class="pd-btn pd-btn--primary" href="/providers/compare?ids={{this.compareIds}}">
              Compare Now
            </a>
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
